import * as React from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import BaseCheckboxInput from './BaseCheckboxInput'

export type CheckboxInputLabelProps = {
  checked?: boolean
  disabled?: boolean
  invalid?: boolean
  variant?: 'primary' | 'secondary'
  highlight?: boolean
}

export type CheckboxInputProps = {
  checkedByDefault?: boolean
  className?: string
  value?: string
  label: string | React.ReactNode
  loading?: boolean
  subtext?: string
  onClick?: React.InputHTMLAttributes<HTMLInputElement>['onClick']
  onChange?: React.InputHTMLAttributes<HTMLInputElement>['onChange']
  tooltip?: React.ReactNode
} & CheckboxInputLabelProps

const StyledLabel = styled.label<CheckboxInputLabelProps>`
  border-radius: 1rem;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
  position: relative;

  & > span {
    display: flex;
    flex-direction: row;
    align-items: flex-start;

    & > svg {
      flex-basis: 1.5rem;
      flex-shrink: 0;
    }
  }

  ${props => {
    switch (props.variant) {
      case 'secondary':
        return css`
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: ${props.theme.spacing.m};
          height: 100%;
          background-color: ${props.theme.colors.formFieldBackground};
          border: 2px solid ${props.theme.colors.formFieldBackground};
          ${props.checked &&
          css`
            background-color: ${props.theme.colors.background};
            border: 2px solid ${props.theme.colors.checkboxSelected};
          `}
          ${props.theme.typography.button}
          color: ${props.theme.colors.label};
        `
      case 'primary':
      default:
        return css`
          ${props.theme.typography.body}
          color: ${props.theme.colors.secondaryLabel};
        `
    }
  }}

  ${props =>
    props.disabled &&
    css`
      color: ${props.theme.colors.disabledLabel};
    `}
  ${props =>
    props.invalid &&
    css`
      border-color: ${props.theme.colors.error};
    `}
    ${props =>
    props.highlight &&
    props.checked &&
    css`
      background-color: ${props.theme.colors.bannerBackgroundSuccess};
    `}
`

const CheckboxInput = React.forwardRef<HTMLInputElement, CheckboxInputProps>(
  (
    { checked, value, disabled, loading, invalid, label, subtext, variant, tooltip, highlight, className, ...props },
    ref
  ) => {
    return (
      <StyledLabel
        {...{ checked, disabled, invalid, variant, highlight }}
        className={className}
        data-testid="checkbox-input"
      >
        <BaseCheckboxInput
          ref={ref}
          loading={loading}
          variant={variant}
          label={label}
          tooltip={tooltip}
          subtext={subtext}
          {...props}
          {...{ value, checked, disabled }}
        />
      </StyledLabel>
    )
  }
)

export default CheckboxInput
